import { useEffect, useRef } from "react";
import { useLatest } from "../../../hooks/useLatest";

// Module copy of ChatLayout's pure key helper (same pattern as useFavorites / useAndroidShare).
const fq = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);

type UseFavoritePreloadsOptions = {
  /** Pinned ids: channel ids ("space:general") and kanban-list ids ("klist:<space>:<channel>:<list>"). */
  favorites: string[];
  parseListFav: (fid: string) => { fqid: string } | null;
  /** Loaded kanban lists per channel (a key present with [] means "loaded, but empty"). */
  kanbanByChan: Record<string, unknown[] | undefined>;
  /** Message previews per channel. */
  previewsByChan: Record<string, unknown>;
  /** Messages per channel. */
  msgsByKey: Record<string, unknown[] | undefined>;
  loadKanbanIfNeeded: (fqid: string) => void;
  reloadKanban: (fqid: string) => void;
  loadPreviewIfNeeded: (fqid: string) => void;
};

/**
 * Warms the data behind the home screen's favourites so the cards are filled in before you open them:
 * kanban lists for favourited lists, a short message preview for favourited channels, and one retry for
 * a favourited list that came back empty.
 *
 * Moved out of ChatLayout with the same behaviour. The two kanban effects overlap on purpose (the first
 * loads whenever the favourites change, the second whenever kanban data changes) and are kept as they were.
 *
 * Returns `kanbanReloadedRef`, the set of channels already retried; the "Retry" button on a favourite card
 * removes its channel from it so the automatic retry can run again.
 */
export function useFavoritePreloads({
  favorites,
  parseListFav,
  kanbanByChan,
  previewsByChan,
  msgsByKey,
  loadKanbanIfNeeded,
  reloadKanban,
  loadPreviewIfNeeded,
}: UseFavoritePreloadsOptions) {
  // These effects run when their data changes; call the newest functions at that moment.
  const loadKanbanIfNeededRef = useLatest(loadKanbanIfNeeded);
  const reloadKanbanRef = useLatest(reloadKanban);
  const loadPreviewIfNeededRef = useLatest(loadPreviewIfNeeded);
  const parseListFavRef = useLatest(parseListFav);

  // Ensure kanban data is loaded for any favorited lists
  useEffect(() => {
    for (const fid of favorites) {
      if (fid.startsWith('klist:')) {
        const parts = fid.split(':');
        const vId = parts[1]; const cId = parts[2];
        if (vId && cId) loadKanbanIfNeededRef.current(fq(vId, cId));
      }
    }
  }, [favorites, loadKanbanIfNeededRef]);

  // Proactively load kanban data for any favorited lists (works on landing)
  useEffect(() => {
    try {
      const need: string[] = [];
      for (const fid of favorites) {
        if (fid.startsWith('klist:')) {
          const parsed = parseListFavRef.current(fid);
          if (parsed) {
            const fqid = parsed.fqid;
            if (!(fqid in kanbanByChan)) need.push(fqid);
          }
        }
      }
      // De-dupe
      Array.from(new Set(need)).forEach(id => { loadKanbanIfNeededRef.current(id); });
    } catch { /* noop */ }
  }, [favorites, kanbanByChan, loadKanbanIfNeededRef, parseListFavRef]);

  // Proactively load message previews for favorited channels
  useEffect(() => {
    try {
      for (const fid of favorites) {
        if (fid.startsWith('klist:')) continue; // handled separately
        const [vId, cId] = fid.split(':');
        if (!vId || !cId) continue;
        const fqid = fq(vId, cId);
        const msgs = msgsByKey[fqid];
        const haveMsgs = Array.isArray(msgs) && msgs.length > 0;
        const havePrev = Object.prototype.hasOwnProperty.call(previewsByChan, fqid);
        if (!haveMsgs && !havePrev) {
          loadPreviewIfNeededRef.current(fqid);
        }
      }
    } catch { /* noop */ }
  }, [favorites, previewsByChan, msgsByKey, loadPreviewIfNeededRef]);

  // If kanban exists but came back empty (due to timing or transient error), try one reload per channel
  const kanbanReloadedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    try {
      for (const fid of favorites) {
        if (!fid.startsWith('klist:')) continue;
        const parsed = parseListFavRef.current(fid); if (!parsed) continue;
        const { fqid } = parsed;
        const lists = kanbanByChan[fqid];
        if (Array.isArray(lists) && lists.length === 0 && !kanbanReloadedRef.current.has(fqid)) {
          kanbanReloadedRef.current.add(fqid);
          reloadKanbanRef.current(fqid);
        }
      }
    } catch { /* noop */ }
  }, [favorites, kanbanByChan, reloadKanbanRef, parseListFavRef]);

  return { kanbanReloadedRef };
}

